import {
  Box,
  Slider,
  type SliderProps,
  Stack,
  Typography,
} from "@mui/material";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

const DEFAULT_INPUT_MARGIN_COMPENSATION = -1;

type Props<
  FormType extends FieldValues,
  TransformedFieldValues = FormType,
> = SliderProps & {
  label: string;
  description?: string;
  inputEndAdornment?: string;
  name: FieldPath<FormType>;
  control: Control<FormType, unknown, TransformedFieldValues>;
  inputWidth?: number;
  min?: number;
  max?: number;
  step?: number;
  error?: boolean;
  onAfterChange: () => void;
};

export const FormInputWithSlider = <T extends object, V extends object>({
  name,
  label,
  control,
  inputEndAdornment,
  min = 1,
  max = 10_000,
  step = 50,
  onAfterChange,
  error,
}: Props<T, V>) => (
  <Stack>
    <Typography component="label" htmlFor={name} variant="body1">
      {label}
    </Typography>
    <Box display="flex" alignItems="center" gap={2}>
      <Box flexGrow={1}>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Box px={2} display="flex" alignItems="center">
              <Slider
                {...field}
                value={field.value ?? min}
                min={min}
                max={max}
                step={step}
                onChange={(_e, value) => {
                  field.onChange(value);
                  onAfterChange();
                }}
                color={error ? "error" : "primary"}
                sx={{
                  "& .MuiSlider-rail": {
                    backgroundColor: error ? "auto" : "#80B3FF",
                  },
                }}
              />
            </Box>
          )}
        />
      </Box>
      <Box
        width={({ spacing }) => spacing(21)}
        mt={DEFAULT_INPUT_MARGIN_COMPENSATION}
      >
        <FormInput
          inputEndAdornment={<Typography>{inputEndAdornment}</Typography>}
          disablePadding
          control={control}
          onAfterChange={onAfterChange}
          name={name}
          disableHelperText
          scale={2}
          isNumber
          min={0}
          max={100}
          mask={Number}
        />
      </Box>
    </Box>
  </Stack>
);
